<template>
  <b-modal
    ref="modal"
    :title="title"
    size="lg"
    ok-only
    ok-title="OK"
    :header-bg-variant="headerVariant"
    header-text-variant="white"
  >
    <div v-if="result">
      <div class="text-center mb-4">
        <i
          class="fas"
          :class="isDryRun ? 'fa-clipboard-check text-info' : 'fa-check-circle text-success'"
          style="font-size: 4rem;"
        ></i>
      </div>

      <b-alert v-if="isDryRun" show variant="info" class="mb-3">
        <i class="fas fa-info-circle mr-2"></i>
        Nothing was written. Untick <strong>ตรวจสอบก่อน (dry run)</strong> and submit
        again to import.
      </b-alert>

      <b-card bg-variant="light" class="mb-3">
        <h6 class="text-primary mb-3">
          <i class="fas fa-info-circle mr-2"></i>
          Import Details
        </h6>
        <dl class="row mb-0">
          <dt class="col-sm-5">Total Rows:</dt>
          <dd class="col-sm-7">{{ result.totalRows || 0 }}</dd>
          <template v-if="result.filteredEmptyRows !== undefined">
            <dt class="col-sm-5">Filtered Empty Rows:</dt>
            <dd class="col-sm-7">{{ result.filteredEmptyRows }}</dd>
          </template>
          <dt class="col-sm-5">Imported Count:</dt>
          <dd class="col-sm-7">
            <strong class="text-success">{{ result.importedCount || 0 }}</strong>
          </dd>
          <template v-if="firstDn">
            <dt class="col-sm-5">First DN:</dt>
            <dd class="col-sm-7"><code>{{ firstDn }}</code></dd>
          </template>
          <template v-if="lastDn">
            <dt class="col-sm-5">Last DN:</dt>
            <dd class="col-sm-7"><code>{{ lastDn }}</code></dd>
          </template>
          <template v-if="skippedExisting.length">
            <dt class="col-sm-5">Already in Branch:</dt>
            <dd class="col-sm-7">
              <span class="text-warning">{{ skippedExisting.length }}</span> skipped
            </dd>
          </template>
          <template v-if="generatedDns.length">
            <dt class="col-sm-5">New DN Assigned:</dt>
            <dd class="col-sm-7">
              <span class="text-info">{{ generatedDns.length }}</span> row(s)
            </dd>
          </template>
        </dl>
      </b-card>

      <b-card v-if="generatedDns.length" border-variant="info" class="mb-3">
        <h6 class="text-info mb-3">
          <i class="fas fa-id-card mr-2"></i>
          {{ generatedDns.length }} row(s) imported on a new DN
        </h6>
        <p class="small text-muted">
          Their DN is not YY + 4-digit running, so it could not be converted. They were
          imported all the same, on the next DN in this branch's running number for the
          current year. The original number is kept in <code>existDn</code>.
        </p>
        <b-table-simple small responsive class="mb-0">
          <b-thead>
            <b-tr>
              <b-th>Row</b-th>
              <b-th>Old DN</b-th>
              <b-th>New DN</b-th>
              <b-th>Name</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(generated, index) in generatedDns" :key="index">
              <b-td>{{ generated.row }}</b-td>
              <b-td><code>{{ generated.existDn || '—' }}</code></b-td>
              <b-td><code>{{ generated.dn }}</code></b-td>
              <b-td>{{ generated.name || '—' }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-card>

      <b-card v-if="errors.length" border-variant="warning" class="mb-0">
        <h6 class="text-warning mb-3">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          {{ errors.length }} row(s) not imported
        </h6>
        <p class="small text-muted">
          These rows could not be read from the file. Add them by hand after the import.
        </p>
        <b-table-simple small responsive class="mb-0">
          <b-thead>
            <b-tr>
              <b-th>Row</b-th>
              <b-th>DN</b-th>
              <b-th>Name</b-th>
              <b-th>Reason</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(error, index) in errors" :key="index">
              <b-td>{{ error.row }}</b-td>
              <b-td><code>{{ error.dn || '—' }}</code></b-td>
              <b-td>{{ error.name || '—' }}</b-td>
              <b-td class="small">{{ error.message }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-card>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'ImportResultModal',
  data() {
    return {
      result: null
    };
  },
  computed: {
    isDryRun() {
      return Boolean(this.result && this.result.dryRun);
    },
    title() {
      if (!this.result) return 'Import Result';
      return this.isDryRun ? 'Dry Run — Nothing Written' : 'Patients Imported Successfully!';
    },
    headerVariant() {
      return this.isDryRun ? 'info' : 'success';
    },
    // The API returns firstDn/lastDn; earlier markup read firstDN/lastDN and so
    // never rendered them.
    firstDn() {
      return this.result && (this.result.firstDn || this.result.firstDN);
    },
    lastDn() {
      return this.result && (this.result.lastDn || this.result.lastDN);
    },
    errors() {
      return (this.result && this.result.errors) || [];
    },
    skippedExisting() {
      return (this.result && this.result.skippedExisting) || [];
    },
    // Rows whose legacy DN was unreadable: imported on a freshly issued DN
    // rather than left out, so they are reported as information, not an error.
    generatedDns() {
      return (this.result && this.result.generatedDns) || [];
    }
  },
  methods: {
    show(result) {
      this.result = result;
      this.$refs.modal.show();
    }
  }
};
</script>
